import type {
  ColumnSchema,
  CrossRule,
  ExpectedColumn,
  InvalidRow,
  Row,
  RowIssue,
  ValidRow,
  ValidationResult,
} from './types';

export function validate(
  rows: Row[],
  schema: ColumnSchema[],
  crossRules?: CrossRule[],
): ValidationResult {
  const validRows: ValidRow[] = [];
  const invalidRows: InvalidRow[] = [];

  for (let i = 0; i < rows.length; i++) {
    const issues = collectIssues({ row: rows[i], index: i, allRows: rows, schema, crossRules });

    if (issues.length) {
      invalidRows.push({ rowIndex: i, data: rows[i], issues });
    } else {
      validRows.push({ rowIndex: i, data: rows[i] });
    }
  }

  return {
    summary: {
      total: rows.length,
      valid: validRows.length,
      invalid: invalidRows.length,
    },
    validRows,
    invalidRows,
  };
}

type CollectIssuesParams = {
  row: Row;
  index: number;
  allRows: Row[];
  schema: ColumnSchema[];
  crossRules?: CrossRule[];
};

function collectIssues({
  row,
  index,
  allRows,
  schema,
  crossRules,
}: CollectIssuesParams): RowIssue[] {
  const issues: RowIssue[] = [];

  for (const { column, rules, warnRules } of schema) {
    const value = row[column];

    for (const rule of rules ?? []) {
      const msg = rule(value, row, index, allRows);
      if (msg) issues.push({ column, message: msg, type: 'error' });
    }

    for (const rule of warnRules ?? []) {
      const msg = rule(value, row, index, allRows);
      if (msg) issues.push({ column, message: msg, type: 'warning' });
    }
  }

  for (const rule of crossRules ?? []) {
    const issue = rule(row, index, allRows);
    if (issue) issues.push(issue);
  }

  return issues;
}

export function columnsMatch(
  fileHeaders: string[],
  expected: ExpectedColumn[],
  messages: { missing: string },
): RowIssue[] | null {
  const missing = expected.filter((e) => e.required && !fileHeaders.includes(e.name));

  if (!missing.length) return null;

  return missing.map((e) => ({
    column: e.name,
    message: messages.missing,
    type: 'error' as const,
  }));
}
